import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import type { ContactResponse } from '@troia-v3/types';
import { Button } from '@/shared/components/ui/Button';
import { Input, Select } from '@/shared/components/ui';
import { formatPhoneNumber } from '@/shared/utils';
import { Icon } from '@/shared/icons'
import { usePrivacy } from '@/contexts/PrivacyContext'
interface MergeContactsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onMerge: (sourceContactId: string, targetContactId: string, name: string) => void;
  contacts: ContactResponse[];
  isLoading?: boolean;
}

/**
 * Dialog para mesclar dois contatos
 *
 * Permite selecionar contato de origem (será removido) e contato de destino (receberá os dados),
 * além de definir o nome final do contato mesclado.
 *
 * @example
 * <MergeContactsDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onMerge={handleMergeContacts}
 *   contacts={contactsList}
 * />
 */
const MergeContactsDialog: React.FC<MergeContactsDialogProps> = ({
  isOpen,
  onClose,
  onMerge,
  contacts,
  isLoading = false,
}) => {
  const { isPrivacyMode } = usePrivacy();
  const [sourceContactId, setSourceContactId] = useState<string>('');
  const [targetContactId, setTargetContactId] = useState<string>('');
  const [mergedName, setMergedName] = useState<string>('');
  const [errors, setErrors] = useState<{
    sourceContact?: string;
    targetContact?: string;
    name?: string;
  }>({});

  const validateForm = (): boolean => {
    const newErrors: {
      sourceContact?: string;
      targetContact?: string;
      name?: string;
    } = {};

    // Contato de origem obrigatório
    if (!sourceContactId) {
      newErrors.sourceContact = 'Selecione o contato de origem';
    }

    // Contato de destino obrigatório
    if (!targetContactId) {
      newErrors.targetContact = 'Selecione o contato de destino';
    }

    // Não pode ser o mesmo contato
    if (sourceContactId && targetContactId && sourceContactId === targetContactId) {
      newErrors.targetContact = 'Os contatos devem ser diferentes';
    }

    // Nome obrigatório
    if (!mergedName.trim()) {
      newErrors.name = 'Nome do contato mesclado é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onMerge(sourceContactId, targetContactId, mergedName);
  };

  const handleClose = () => {
    // Reset form
    setSourceContactId('');
    setTargetContactId('');
    setMergedName('');
    setErrors({});
    onClose();
  };

  // Auto-preencher nome quando selecionar contatos
  const handleSourceChange = (contactId: string) => {
    setSourceContactId(contactId);
    if (errors.sourceContact) {
      setErrors((prev) => ({ ...prev, sourceContact: undefined }));
    }

    // Se ainda não tiver nome definido, sugerir o nome do source
    if (!mergedName && contactId) {
      const contact = contacts.find(c => c.id === contactId);
      if (contact) {
        setMergedName(contact.name);
      }
    }
  };

  const handleTargetChange = (contactId: string) => {
    setTargetContactId(contactId);
    if (errors.targetContact) {
      setErrors((prev) => ({ ...prev, targetContact: undefined }));
    }
  };

  const handleNameChange = (value: string) => {
    setMergedName(value);
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  if (!isOpen) return null;

  const sourceContact = contacts.find(c => c.id === sourceContactId);
  const targetContact = contacts.find(c => c.id === targetContactId);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-[60] p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      /* form: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
    >
      <div
        className="rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: '#ffffff',
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-error-color': '#ef4444',
        } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b sticky top-0"
          style={{
            borderColor: '#e5e7eb',
            backgroundColor: '#ffffff',
          }}
        >
          <h2
            className="text-lg font-semibold flex items-center gap-2"
            style={{ color: '#1e293b' }}
          >
            <Icon name="user-plus" className="w-5 h-5" />
            Mesclar Contatos
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded transition-colors"
            disabled={isLoading}
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 border-b border-blue-100">
          <div className="flex items-start gap-2">
            <Icon name="alert-circle" className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Como funciona o merge:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>O contato de <strong>origem</strong> será removido</li>
                <li>Os dados serão mesclados no contato de <strong>destino</strong></li>
                <li>Você pode definir o nome final do contato mesclado</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Contato de Origem */}
          <div>
            <Select
              label="Contato de Origem"
              required
              value={sourceContactId}
              onChange={(val) => handleSourceChange(val)}
              placeholder="Selecione o contato de origem"
              options={contacts
                .filter(c => c.id !== targetContactId)
                .map(contact => ({
                  value: contact.id,
                  label: isPrivacyMode
                    ? `${contact.name} - ••••••••`
                    : `${contact.name} - ${contact.identifiers.phone[0] ? formatPhoneNumber(contact.identifiers.phone[0]) : contact.identifiers.email[0]}`
                }))}
              error={errors.sourceContact}
              disabled={isLoading}
            />
            {sourceContact && (
              <p
                className="mt-1 text-xs"
                style={{ color: '#64748b' }}
              >
                Este contato será removido após a mesclagem
              </p>
            )}
          </div>

          {/* Contato de Destino */}
          <div>
            <Select
              label="Contato de Destino"
              required
              value={targetContactId}
              onChange={(val) => handleTargetChange(val)}
              placeholder="Selecione o contato de destino"
              options={contacts
                .filter(c => c.id !== sourceContactId)
                .map(contact => ({
                  value: contact.id,
                  label: isPrivacyMode
                    ? `${contact.name} - ••••••••`
                    : `${contact.name} - ${contact.identifiers.phone[0] ? formatPhoneNumber(contact.identifiers.phone[0]) : contact.identifiers.email[0]}`
                }))}
              error={errors.targetContact}
              disabled={isLoading}
            />
            {targetContact && (
              <p
                className="mt-1 text-xs"
                style={{ color: '#64748b' }}
              >
                Este contato receberá os dados mesclados
              </p>
            )}
          </div>

          {/* Nome do Contato Mesclado */}
          <div>
            <label
              className="block text-sm font-medium mb-1"
              style={{ color: '#1e293b' }}
            >
              Nome do Contato Mesclado <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={mergedName}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Nome final do contato"
              error={errors.name}
              disabled={isLoading}
            />
          </div>

          {/* Preview da Mesclagem */}
          {sourceContact && targetContact && mergedName && (
            <div
              className="p-3 border rounded-lg"
              style={{
                backgroundColor: '#f1f5f9',
                borderColor: '#e5e7eb',
              }}
            >
              <p
                className="text-sm font-medium mb-2"
                style={{ color: '#1e293b' }}
              >
                Preview da Mesclagem:
              </p>
              <div
                className="space-y-1 text-sm"
                style={{ color: '#64748b' }}
              >
                <p>
                  <span className="line-through text-red-500">{sourceContact.name}</span> +{' '}
                  <span className="text-green-600">{targetContact.name}</span> ={'>'}{' '}
                  <span className="font-semibold text-indigo-600">{mergedName}</span>
                </p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              variant="secondary"
              size="md"
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              variant="primary"
              size="md"
              loading={isLoading}
              className="flex-1"
            >
              {isLoading ? 'Mesclando...' : 'Mesclar Contatos'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default MergeContactsDialog;
